import secondClearForm from "@iewc/Store/Handlers/CreateAccount/ClearForm";
import clearForm from "@iewc/Store/Handlers/InstantRegistrationForm/ClearForm";
import setFieldValue from "@iewc/Store/Handlers/InstantRegistrationForm/SetFieldValue";
import submitInstantRegistrationForm from "@iewc/Store/Handlers/InstantRegistrationForm/SubmitInstantRegistrationForm";
import { CustomState } from "@iewc/Store/Reducers";
import AccountLink from "@iewc/Widgets/CreateAccount/InstantRegistration/AccountLink";
import AccountNumber from "@iewc/Widgets/CreateAccount/InstantRegistration/AccountNumber";
import ConfirmEmail from "@iewc/Widgets/CreateAccount/InstantRegistration/ConfirmEmail";
import Email from "@iewc/Widgets/CreateAccount/InstantRegistration/Email";
import FirstName from "@iewc/Widgets/CreateAccount/InstantRegistration/FirstName";
import LastName from "@iewc/Widgets/CreateAccount/InstantRegistration/LastName";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { get } from "@insite/client-framework/Services/ApiService";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successLink = "successLink",
    dontKnowAccountLink = "dontKnowAccountLink",
    leadSource = "leadSource",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successLink]: LinkFieldValue;
        [fields.dontKnowAccountLink]: LinkFieldValue;
        [fields.leadSource]: string;
    };
}

const mapStateToProps = (state: CustomState) => ({
    accountNumber: state.customInstantRegistrationForm.fieldValues.accountNumber,
});

const mapDispatchToProps = {
    submitInstantRegistrationForm,
    clearForm,
    secondClearForm,
    setFieldValue,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA> },
        boolean
    >(validateReCaptcha),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InstantRegistrationFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    captchaWrapper?: InjectableCss;
    captchaLabel?: TypographyPresentationProps;
    continueButton?: ButtonPresentationProps;
    dontKnowItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    spinner?: LoadingSpinnerProps;
}

export const instantRegistrationFormStyles: InstantRegistrationFormStyles = {
    container: {
        gap: 0,
        css: css`
            padding-top: 30px;
            padding-bottom: 30px;

            /* stylelint-disable-next-line */
            #requestQuoteForm {
                width: 100%;
            }
        `,
    },
    formGridItem: { width: 12 },
    submitButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    messageTextGridItem: { width: 12 },
    captchaWrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
    captchaLabel: {
        variant: "body",
        forwardAs: "label",
        weight: "bold",
        css: css`
            display: block;
            margin-bottom: 6px;
        `,
    },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    dontKnowItem: {
        css: css`
            flex-direction: column;
            align-items: flex-start;

            @media (max-width: 767px) {
                padding-top: 0;
            }
        `,
    },
    innerContainer: {
        css: css`
            padding-bottom: 30px;
        `,
    },
    spinner: {
        size: 28,
        css: css`
            margin-left: 10px;
        `,
    },
};

const styles = instantRegistrationFormStyles;

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const InstantRegistrationFormContext = React.createContext<ContextType>({
    validators: {},
});

const InstantRegistrationForm = ({
    id,
    fields: widgetFields,
    submitInstantRegistrationForm: widgetSubmitRequestQuoteForm,
    validateReCaptcha: widgetValidateReCaptcha,
    setFieldValue: widgetSetFieldValue,
    clearForm,
    secondClearForm,
    accountNumber,
}: Props) => {
    const { emailRecipients, submitButtonText, leadSource } = widgetFields;
    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();
    const history = useHistory();
    const link = useGetLink(widgetFields.successLink);
    const accountlink = useGetLink(widgetFields.dontKnowAccountLink);
    const { current: Fields } = useRef({
        ConfirmEmail,
        Email,
        AccountNumber,
        FirstName,
        LastName,
        AccountLink,
    } as any);

    useEffect(() => {
        widgetSetFieldValue({ key: "dontKnowAccountLink", value: `${accountlink.url}` });
    }, [accountlink]);

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {        
        event.preventDefault();
        const widgetValidators: Validator[] = [];
        const widgetValidatorKeys = Object.keys(validators);

        widgetValidatorKeys.forEach(key => {
            const validator = validators[key];
            if (validator) {
                widgetValidators.push(validator);
            }
        });
        const results = widgetValidators.map(v => v());
        if (!results.every(o => o)) {
            return;
        }

        const isReCaptchaValid = await widgetValidateReCaptcha({ location: "ContactUs", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }
        let validAccount;
        if (accountNumber) {
            widgetSetFieldValue({ key: "validAccountNumber", value: "" });
            validAccount = await validateAccount(accountNumber);
        }

        if (!validAccount) {
            widgetSetFieldValue({ key: "validAccountNumber", value: validAccount });
            return;
        }

        setIsSubmitting(true);
        widgetSubmitRequestQuoteForm({
            emailRecipients,
            leadSource,
            onSuccess: () => {
                setIsSubmitting(false);
                clearForm();
                secondClearForm();
                if (link.url) {
                    history.push(link.url);
                }
            },
            onComplete() {
                this.onSuccess?.();
            },
        });
    };

    const validateAccount = async (accountNumber: string) => {
        const res = (await get(`api/v1/instantregistration/validateaccount/${accountNumber}`)) as any;
        return res.IsValid;
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.formGridItem}>
                <InstantRegistrationFormContext.Provider value={{ validators }}>
                    <form id="instantRegistrationForm" onSubmit={handleFormSubmit} noValidate>
                        <Typography variant="h2" as="h2">
                            {translate("User Information")}
                        </Typography>
                        <GridContainer {...styles.innerContainer}>
                            <GridItem width={[12, 12, 6, 6, 6]}>
                                <Fields.AccountNumber />
                            </GridItem>
                            <GridItem {...styles.dontKnowItem} width={[12, 12, 6, 6, 6]}>
                                <Fields.AccountLink />
                            </GridItem>
                            <GridItem width={[12, 12, 6, 6, 6]}>
                                <Fields.FirstName />
                            </GridItem>
                            <GridItem width={[12, 12, 6, 6, 6]}>
                                <Fields.LastName />
                            </GridItem>
                            <GridItem width={[12, 12, 6, 6, 6]}>
                                <Fields.Email />
                            </GridItem>
                            <GridItem width={[12, 12, 6, 6, 6]}>
                                <Fields.ConfirmEmail />
                            </GridItem>
                        </GridContainer>
                        <ReCaptcha recaptchaRef={recaptchaRef} location="ContactUs" />
                        <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                            {submitButtonText}
                        </Button>
                        {isSubmitting && (
                                <LoadingSpinner {...styles.spinner} />
                        )}
                    </form>
                </InstantRegistrationFormContext.Provider>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InstantRegistrationForm),
    definition: {
        group: "Instant Registration Form" as any,
        icon: "FormField",
        displayName: "Instant Registration Form",
        fieldDefinitions: [
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Submit",
            },
            {
                name: fields.successLink,
                displayName: "Redirect on success",
                editorTemplate: "LinkField",
                fieldType: "Translatable",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
            },
            {
                name: fields.dontKnowAccountLink,
                displayName: "Don't know account link",
                editorTemplate: "LinkField",
                fieldType: "Translatable",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: false,
            },
        ],
    },
};

export default widgetModule;
